import React, { useState, useEffect } from 'react';
import FormModal from '../../../components/shared/FormModal';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import EmptyState from '../../../components/shared/EmptyState';
import { toast } from '../../../components/shared/toast';
import { apiClient } from '../../../api/apiClient';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../../utils/usePermissions';
import {
  GET_USERS_URL,
  CREATE_USER_URL,
  UPDATE_USER_URL,
  DELETE_USER_URL,
  GET_ROLES_URL
} from '../../../api/settings';

function UsersSubTab({ searchQuery }) {
  const { user: currentUser } = useAuth();
  const { canEdit } = usePermissions();
  const canEditSettings = canEdit('settings');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  // Form Fields
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState('');
  const [userStatus, setUserStatus] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const res = await apiClient(`${GET_USERS_URL}?search=${encodeURIComponent(searchQuery || '')}`);
      setUsers(res?.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await apiClient(GET_ROLES_URL);
      setRoles(res?.roles || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchQuery]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleOpenAddModal = () => {
    setUserToEdit(null);
    setFirstname('');
    setLastname('');
    setEmail('');
    setUsername('');
    setPassword('');
    setRoleId(roles[0]?.id || '');
    setUserStatus(1);
    setShowModal(true);
  };

  const handleOpenEditModal = (user) => {
    setUserToEdit(user);
    setFirstname(user.firstname || '');
    setLastname(user.lastname || '');
    setEmail(user.email || '');
    setUsername(user.username || '');
    setPassword('');
    setRoleId(user.user_role_id || '');
    setUserStatus(user.status !== undefined ? user.status : 1);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!firstname.trim() || !lastname.trim() || !email.trim() || !username.trim()) {
      toast.warning('Please fill in all required user details.');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        email: email.trim(),
        username: username.trim(),
        password: password ? password.trim() : undefined,
        user_role_id: roleId ? parseInt(roleId, 10) : null,
        status: userStatus
      };

      if (userToEdit) {
        await apiClient(UPDATE_USER_URL(userToEdit.id), {
          method: 'PUT',
          body: payload
        });
        toast.success('User updated successfully.');
      } else {
        await apiClient(CREATE_USER_URL, {
          method: 'POST',
          body: payload
        });
        toast.success('User created successfully (Status set to 1).');
      }

      setShowModal(false);
      fetchUsers();
    } catch (err) {
      console.error('Error saving user:', err);
      toast.error(err.message || 'Failed to save user.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await apiClient(DELETE_USER_URL(userToDelete.id), {
        method: 'DELETE'
      });
      toast.success('User deleted successfully.');
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error('Failed to delete user.');
    }
  };

  const displayedUsers = users.filter((u) => u.id !== currentUser?.id);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Top Section & Add Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
          User Accounts
        </h2>
        {canEditSettings && (
          <button
            type="button"
            className="action-btn-primary"
            onClick={handleOpenAddModal}
            style={{ padding: '10px 20px', fontSize: '14px', fontWeight: '600' }}
          >
            + Add New User
          </button>
        )}
      </div>

      {/* Users Table */}
      {isLoading ? (
        <LoadingOverlay isLoading={true} message="Loading Users..." />
      ) : displayedUsers.length === 0 ? (
        <EmptyState title="No Other Users Found" message="Add a new user to grant platform access." />
      ) : (
        <div className="table-responsive-container" style={{ backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '14px 20px', fontWeight: '700', color: '#475569' }}>Name</th>
                <th style={{ padding: '14px 20px', fontWeight: '700', color: '#475569' }}>Username</th>
                <th style={{ padding: '14px 20px', fontWeight: '700', color: '#475569' }}>Email</th>
                <th style={{ padding: '14px 20px', fontWeight: '700', color: '#475569' }}>User Role</th>
                <th style={{ padding: '14px 20px', fontWeight: '700', color: '#475569' }}>Status</th>
                <th style={{ padding: '14px 20px', fontWeight: '700', color: '#475569', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.map((u) => (
                <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.15s ease' }}>
                  <td style={{ padding: '14px 20px', fontWeight: '600', color: '#0f172a' }}>
                    {u.firstname} {u.lastname}
                  </td>
                  <td style={{ padding: '14px 20px', color: '#334155', fontWeight: '500' }}>
                    @{u.username}
                  </td>
                  <td style={{ padding: '14px 20px', color: '#64748b' }}>
                    {u.email}
                  </td>
                  <td style={{ padding: '14px 20px' }}>
                    <span style={{ backgroundColor: '#eff6ff', color: '#0b66e3', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700' }}>
                      {u.role_name || 'No Role'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 20px' }}>
                    <span
                      style={{
                        backgroundColor: u.status === 1 ? '#ecfdf5' : '#fef2f2',
                        color: u.status === 1 ? '#059669' : '#ef4444',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '700'
                      }}
                    >
                      {u.status === 1 ? 'Active (1)' : 'Inactive (0)'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                      {canEditSettings && (
                        <button
                          type="button"
                          onClick={() => handleOpenEditModal(u)}
                          style={{ background: 'none', border: 'none', color: '#0b66e3', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                        >
                          Edit
                        </button>
                      )}
                      {canEditSettings && (
                        <button
                          type="button"
                          onClick={() => setUserToDelete(u)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* User Create / Edit Form Modal */}
      {showModal && (
        <FormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={userToEdit ? 'Edit User' : 'Create New User'}
          className="modal-medium"
        >
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  First Name *
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  placeholder="e.g. John"
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  placeholder="e.g. Doe"
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  Username *
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. johndoe"
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. john@ransilu.com"
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  User Role *
                </label>
                <select
                  className="form-input"
                  value={roleId}
                  onChange={(e) => setRoleId(e.target.value)}
                  required
                >
                  <option value="">Select Role...</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.role_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  {userToEdit ? 'Password (Leave blank to keep)' : 'Password'}
                </label>
                <input
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={userToEdit ? '••••••••' : 'Enter password'}
                />
              </div>
            </div>

            {userToEdit && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  Status
                </label>
                <select
                  className="form-input"
                  value={userStatus}
                  onChange={(e) => setUserStatus(parseInt(e.target.value, 10))}
                >
                  <option value={1}>Active (1)</option>
                  <option value={0}>Inactive (0)</option>
                </select>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <button
                type="button"
                className="action-btn-outlined"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="action-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : userToEdit ? 'Update User' : 'Create User'}
              </button>
            </div>
          </form>
        </FormModal>
      )}

      {/* Confirmation Modal for Soft Delete */}
      <ConfirmationModal
        isOpen={!!userToDelete}
        type="danger"
        title="Delete User Account"
        message={`Are you sure you want to delete user "${userToDelete?.firstname} ${userToDelete?.lastname}"?`}
        confirmLabel="Delete User"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setUserToDelete(null)}
      />
    </div>
  );
}

export default UsersSubTab;
